// Organizer-facing tournament submission form. Submits as `pending`.
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const BLANK = {
  name: "", start_date: "", end_date: "", location: "",
  format: "5x5", age_categories: "", entry_fee: "", contact_info: "", description: "",
};

// Pulled out of the page component so React doesn't remount the field on every keystroke.
function Field({ k, label, type = "text", testid, value, onChange, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(k, e.target.value)}
        className="w-full mt-2 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
        required
        {...rest}
      />
    </label>
  );
}

export default function SubmitTournament() {
  const { user, loading } = useAuth();
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-12 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        format: form.format || "5x5",
        entry_fee: Number(form.entry_fee || 0),
        age_categories: form.age_categories.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.post("/tournaments", payload);
      toast.success("Submitted! Awaiting admin approval.");
      nav("/tournaments");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Host</div>
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter mt-2">Submit a Tournament</h1>
      <p className="text-sm text-zinc-400 mt-3 max-w-xl">
        Your submission goes to the admin queue. Approved listings appear in the public directory immediately.
      </p>

      <form onSubmit={onSubmit} className="mt-10 grid md:grid-cols-2 gap-5">
        <div className="md:col-span-2"><Field k="name" label="Tournament Name" testid="t-name" value={form.name} onChange={set} /></div>
        <Field k="start_date" label="Start Date" type="date" testid="t-start" value={form.start_date} onChange={set} />
        <Field k="end_date" label="End Date" type="date" testid="t-end" value={form.end_date} onChange={set} />
        <div className="md:col-span-2"><Field k="location" label="Location (City, State)" testid="t-location" value={form.location} onChange={set} /></div>
        <label className="block">
          <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Format</span>
          <select
            data-testid="t-format"
            value={form.format}
            onChange={(e) => set("format", e.target.value)}
            className="w-full mt-2 bg-zinc-950 border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white appearance-none cursor-pointer"
          >
            <option value="5x5">5x5</option>
            <option value="3x3">3x3</option>
          </select>
        </label>
        <Field k="age_categories" label="Age Categories (comma-separated)" testid="t-ages" value={form.age_categories} onChange={set} placeholder="U14, U16, Open" />
        <Field k="entry_fee" label="Entry Fee (USD)" type="number" testid="t-fee" value={form.entry_fee} onChange={set} min="0" />
        <div className="md:col-span-2"><Field k="contact_info" label="Contact Email / Phone" testid="t-contact" value={form.contact_info} onChange={set} /></div>

        <label className="block md:col-span-2">
          <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Description</span>
          <textarea
            data-testid="t-description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={5}
            className="w-full mt-2 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
          />
        </label>

        <div className="md:col-span-2 flex gap-3 pt-2">
          <button
            data-testid="t-submit-btn"
            type="submit"
            disabled={saving}
            className="h-12 px-6 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
          >{saving ? "Submitting…" : "Submit for Review"}</button>
          <button type="button" onClick={() => setForm(BLANK)} className="h-12 px-6 border border-zinc-800 text-zinc-400 hover:text-white text-xs font-bold tracking-widest uppercase">Reset</button>
        </div>
      </form>
    </div>
  );
}
